import { produce } from 'immer'
import { create } from 'zustand'

type NewClusterFormState = {
	isFormValid: boolean
	formValues: Record<string, any>
}

type NewClusterFormActions = {
	actions: {
		clearFormValues: () => void
		setFormValues: (fieldName: string, value: any) => void
		setFormValidity: (isValid: boolean) => void
	}
}

const DEFAULT_READONLY_EKS_ADDON_KEYS = new Set([
	'aws_load_balancer_controller',
	'karpenter',
	'metrics_server',
	'external_secrets'
])

const defaultAwsEksAddonsConfig = {
	aws_load_balancer_controller: true,
	karpenter: true,
	metrics_server: true,
	external_secrets: true,
	argo_rollouts: false,
	argo_workflows: false,
	calico: false,
	cert_manager: false,
	cluster_autoscaler: false,
	external_dns: false,
	kubecost: false,
	kyverno: false,
	falcon_sensor: false
}

const initialFormValues = {
	distributionList: '',
	application: '',
	environment: '',
	accountID: '',
	deployment: '',
	dataClassification: '',
	tierLevel: '',
	aws_eks_custom_addons_config: defaultAwsEksAddonsConfig
}

const newClusterFormStore = create<NewClusterFormState & NewClusterFormActions>(
	(set, _get) => ({
		isFormValid: false,
		formValues: initialFormValues,
		actions: {
			clearFormValues: () => set(() => ({ formValues: initialFormValues })),
			setFormValidity: (isValid: boolean) => set(() => ({ isFormValid: isValid })),
			setFormValues: (fieldName: string, value: any) =>
				set((state) =>
					produce(state, (draft) => {
						if (fieldName === 'aws_eks_custom_addons_config') {
							if (value && typeof value === 'object' && !Array.isArray(value)) {
								const incoming = value as Record<string, boolean>
								const merged = { ...(draft.formValues.aws_eks_custom_addons_config as Record<string, boolean>) }
								for (const [key, checked] of Object.entries(incoming)) {
									if (DEFAULT_READONLY_EKS_ADDON_KEYS.has(key)) {
										merged[key] = true
									} else {
										merged[key] = Boolean(checked)
									}
								}
								draft.formValues.aws_eks_custom_addons_config = merged
							}
							return
						}
						if (fieldName.startsWith('aws_eks_custom_addons_config.')) {
							const key = fieldName.split('.')[1]
							if (key && typeof draft.formValues.aws_eks_custom_addons_config === 'object') {
								if (DEFAULT_READONLY_EKS_ADDON_KEYS.has(key)) {
									;(draft.formValues.aws_eks_custom_addons_config as Record<string, boolean>)[key] = true
								} else {
									;(draft.formValues.aws_eks_custom_addons_config as Record<string, boolean>)[key] = Boolean(value)
								}
							}
							return
						}
						draft.formValues[fieldName] = value
						if (
							value &&
							typeof value === 'object' &&
							'value' in value
						) {
							if (
								fieldName === 'distributionList' ||
								fieldName === 'environment' ||
								fieldName === 'deployment' ||
								fieldName === 'dataClassification' ||
								fieldName === 'tierLevel'
							) {
								draft.formValues[fieldName] = value.value
							}
						}
					})
				)
		}
	})
)

export const useNewClusterFormStore = () =>
	newClusterFormStore((state) => state)
